import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTelegram } from '@/hooks/useTelegram'
import { useStudioStore } from '@/store'
import { Header } from './Header'
import { StudioNavigation } from './StudioNavigation'
import { ManagementCompanyNavigation } from './ManagementCompanyNavigation'
import { StudioOwnerNavigation } from './StudioOwnerNavigation'
import { isPedagogyChromelessPath } from '@/lib/pedagogyRoutes'

export function Layout() {
  const { backButton } = useTelegram()
  const location = useLocation()
  const navigate = useNavigate()
  const { studios, selectedStudio, setSelectedStudio, setSelectedPartnerNumber, studiosSynced } = useStudioStore()

  useEffect(() => {
    const match = location.pathname.match(/^\/partners\/([^/]+)/)
    if (!match) {
      setSelectedPartnerNumber(null)
      return
    }

    const parsed = Number(match[1])
    if (Number.isNaN(parsed)) {
      setSelectedPartnerNumber(null)
      return
    }
    setSelectedPartnerNumber(parsed)
  }, [location.pathname, setSelectedPartnerNumber])

  useEffect(() => {
    const partnerMatch = location.pathname.match(/^\/partners\/[^/]+\/studios\/(\d+)\/.+/)
    const studioIdRaw = partnerMatch?.[1]

    if (!studioIdRaw) {
      return
    }

    const studioId = Number(studioIdRaw)
    if (Number.isNaN(studioId)) {
      return
    }

    const studio = studios.find((item) => item.studio_internal_id === studioId)
    if (!studio) {
      if (studiosSynced) {
        if (selectedStudio?.studio_internal_id === studioId) {
          setSelectedStudio(null)
        }

        const partnerNumber = location.pathname.match(/^\/partners\/([^/]+)\/studios\/[^/]+\/.+/)?.[1] ?? null
        const hasPartnerStudios = partnerNumber
          ? studios.some((item) => String(item.partner_number ?? '') === partnerNumber)
          : false

        const partnerRows = partnerNumber
          ? studios.filter(item => String(item.partner_number ?? '') === partnerNumber)
          : []
        const teacherOnlyPartner = partnerRows.length > 0 && partnerRows.every(item =>
          item.role === 'teacher' || item.role === 'senior_teacher' || item.access_kind === 'senior_teacher'
        )
        navigate(
          hasPartnerStudios && partnerNumber
            ? teacherOnlyPartner
              ? `/partners/${partnerNumber}/studios/${partnerRows[0].studio_internal_id}/teachers`
              : `/partners/${partnerNumber}/analytics`
            : '/partners',
          { replace: true }
        )
      }
      return
    }

    if (selectedStudio?.studio_internal_id !== studioId) {
      setSelectedStudio(studio)
    }
  }, [location.pathname, navigate, selectedStudio?.studio_internal_id, setSelectedStudio, studios, studiosSynced])

  useEffect(() => {
    const isPartnerStudioPage = /^\/partners\/[^/]+\/studios\/[^/]+\/.+/.test(location.pathname)
    const isStudioPage = isPartnerStudioPage || location.pathname !== '/'
    
    if (isStudioPage) {
      backButton.show()
      backButton.onClick(() => {
        if (isPartnerStudioPage) {
          const match = location.pathname.match(/^\/partners\/([^/]+)\/studios\/[^/]+\/.+/)
          const partnerNumber = match?.[1]
          if (partnerNumber) {
            const studioId = Number(location.pathname.match(/^\/partners\/[^/]+\/studios\/(\d+)\//)?.[1])
            const currentStudio = studios.find(studio => studio.studio_internal_id === studioId)
            if (currentStudio && (
              currentStudio.role === 'teacher' ||
              currentStudio.role === 'senior_teacher' ||
              currentStudio.access_kind === 'senior_teacher'
            )) {
              navigate(`/partners/${partnerNumber}/studios/${currentStudio.studio_internal_id}/teachers`)
              return
            }
            navigate(`/partners/${partnerNumber}/analytics`)
            return
          }
          navigate('/partners')
          return
        }
        navigate(-1)
      })
    } else {
      backButton.hide()
    }

    return () => {
      backButton.offClick(() => {})
    }
  }, [location.pathname, backButton, navigate, studios])

  const isStudioPage =
    /^\/partners\/[^/]+\/studios\/[^/]+\/.+/.test(location.pathname)
  const isManagementCompany = studios.some(studio => studio.access_kind === 'management_company')
  // Определяем, на какой глобальной странице мы находимся
  const isGlobalPage = [
    '/',
    '/partners',
    '/analytics',
    '/management',
    '/settings/notifications',
    '/admin/notifications'
  ].includes(location.pathname) ||
    /^\/partners\/[^/]+\/analytics$/.test(location.pathname) ||
    /^\/partners\/[^/]+\/marketing(\/.*)?$/.test(location.pathname) ||
    /^\/partners\/[^/]+\/management$/.test(location.pathname) ||
    location.pathname.startsWith('/management/')
  const isScreenModePage = location.pathname === '/management-ratings/prototype'
  const isTrainerChecklistReportPage =
    /^\/management\/trainer-checklist\/report\/[^/]+$/.test(location.pathname)
    || /^\/partners\/[^/]+\/studios\/[^/]+\/teachers\/[^/]+\/reviews\/[^/]+$/.test(location.pathname)

  // ⚠️ Литеральный `bg-white` здесь и в <main> ниже — намеренно, а не пропущенное
  // место миграции на тёмную тему: страница тренерского отчёта печатается, а печать
  // в этой миграции принудительно светлая (`.report-print-page` в src/index.css
  // возвращает всю палитру к светлым значениям). Токен `bg-card` дал бы тот же белый
  // под печатью, но тёмный фон при просмотре отчёта на экране в тёмной теме — и лист
  // перестал бы выглядеть листом.
  return (
    <div className={`min-h-screen flex flex-col ${isTrainerChecklistReportPage ? 'bg-white' : 'bg-background'}`}>
      {!isScreenModePage && <Header />}
      <main className={
        isScreenModePage
          ? 'min-h-0 flex-1 overflow-hidden p-2'
          : isTrainerChecklistReportPage
            ? 'flex-1 bg-white px-0 py-0 pb-20'
            : 'flex-1 px-4 py-2 pb-20'
      }>
        <Outlet />
      </main>
      {/* Навигация для страниц студий */}
      {!isScreenModePage && isStudioPage && selectedStudio && <StudioNavigation />}
      {/* Навигация для управляющих компаний */}
      {!isScreenModePage && !isStudioPage && isManagementCompany && <ManagementCompanyNavigation />}
      {/* Навигация для обычных владельцев на глобальных страницах */}
      {!isScreenModePage && !isStudioPage && !isManagementCompany && isGlobalPage && <StudioOwnerNavigation />}
    </div>
  )
}
